/**
 * 콘텐츠 · 플레이리스트 — 화면에 나갈 재료를 만들고 묶는다.
 */
import type { ManualChapter } from '../types.js';

export const CONTENT: ManualChapter = {
  slug: 'content',
  title: '콘텐츠',
  summary: '화면에 재생할 이미지·영상·HTML·PDF·웹 페이지를 올리고, 공지·홍보 화면을 템플릿으로 만듭니다.',
  menuPath: '/content',
  editCapability: 'content:write',
  sections: [
    {
      id: 'kinds',
      title: '콘텐츠의 다섯 가지 종류',
      blocks: [
        {
          kind: 'table',
          header: ['종류', '무엇인가'],
          rows: [
            ['이미지', 'JPEG · PNG · GIF · WebP 파일. PDF 를 올리면 쪽마다 이미지가 됩니다(아래 “PDF” 절).'],
            ['영상', 'MP4 · MOV · WebM · MKV · AVI 파일. 그대로 틀 수 없는 영상은 모든 화면이 트는 모양으로 바꿔 내보냅니다(아래 “영상” 절). 끝까지 재생되면 다음으로 넘어갑니다.'],
            ['웹', '인터넷에 공개된 웹 페이지 주소(https://…)를 그대로 표시합니다.'],
            ['템플릿', '정해진 디자인에 글자만 채워 만드는 공지·홍보·알림 화면'],
            ['HTML', '직접 만든 HTML 파일(.html · .htm)을 올려 화면 크기에 맞춰 띄웁니다(아래 “HTML” 절).'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '콘텐츠를 올리기만 해서는 화면에 나오지 않습니다',
          text: '콘텐츠는 재생에 쓰는 재료입니다. **플레이리스트**에 담고, 그 플레이리스트를 화면이나 스케줄에 배정해야 화면에 나옵니다. 몇 초씩 보여줄지는 콘텐츠가 아니라 **플레이리스트에서** 정합니다 — 같은 콘텐츠라도 플레이리스트마다 노출 시간이 다를 수 있습니다.',
        },
      ],
    },
    {
      id: 'edit',
      title: '콘텐츠 정보 고치기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '카드에 마우스를 올려 [[편집]] 을 누릅니다.', body: '콘텐츠 상세 화면의 [[정보 수정]] 도 같습니다. 목록 보기에서는 줄 오른쪽의 [[편집]] 입니다.' },
            { title: '제목과 태그를 고칩니다.', body: '웹 콘텐츠는 주소도 고칠 수 있습니다. 태그는 쉼표로 구분합니다.' },
            { title: '[[저장]] 을 누릅니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '웹 주소를 바꾸면 재생 중인 화면도 바뀝니다',
          text: '그 웹 콘텐츠가 담긴 플레이리스트를 재생 중인 모든 화면에 곧바로 새 주소가 표시됩니다. 템플릿은 [[편집]] 을 누르면 내용까지 고치는 편집기가 열리고, 그 안 [[기본 정보]] 칸에서 제목·태그도 함께 고칩니다.',
        },
      ],
    },
    {
      id: 'upload',
      title: '이미지·영상 올리기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[콘텐츠]] 메뉴에서 오른쪽 위 [[콘텐츠 추가]] 를 누릅니다.' },
            { title: '태그를 붙이려면 [[파일 업로드]] 를 누르기 전에 입력합니다.', body: '메뉴 맨 위 [[태그]] 칸에 “로비, 가을 프로모션” 처럼 쉼표로 구분해 입력하면 **이번에 올리는 파일 전체**에 붙습니다. 파일을 고르는 순간 업로드가 시작되므로 태그는 그 전에 입력합니다. 나중에 고칠 수도 있습니다.' },
            { title: '[[파일 업로드]] 를 누르고 올릴 파일을 고릅니다.', body: '여러 개를 한꺼번에 골라도 됩니다. 하나씩 차례로 올라갑니다.' },
            { title: '목록에 새 카드가 모두 나타났는지 확인합니다.', body: '파일 이름이 그대로 콘텐츠 제목이 됩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '제목은 올린 뒤에 고칠 수 있습니다',
          text: '“IMG_2031.jpg” 처럼 알아보기 어려운 이름으로 올라갔다면, 카드에 마우스를 올려 [[편집]] 을 누르고 “2026-10 가을 프로모션” 처럼 바꾸세요. 파일 자체는 바꿀 수 없습니다. 다른 파일로 교체하려면 새로 올린 뒤 플레이리스트에서 바꿔 넣습니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '용량이 큰 영상은 올라가지 않을 수 있습니다',
          text: '서버가 받는 한도는 2GB 입니다. 다만 인터넷 주소(클라우드 중계를 거치는 주소)로 콘솔에 접속한 경우 **100MB가 넘는 파일은 중간에서 끊길 수 있습니다.** 그때는 사내망 주소로 접속해 올리거나 설치 담당자에게 문의하세요. 올린 뒤에는 목록에 파일이 실제로 등록되었는지 반드시 확인하세요.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '같은 파일은 두 번 등록되지 않습니다',
          text: '이미 올린 것과 똑같은 파일을 다시 올리면 새로 만들지 않고 기존 콘텐츠를 그대로 사용합니다. 이때 입력한 태그는 그 콘텐츠에 **추가됩니다** — 원래 붙어 있던 태그는 그대로 남습니다.',
        },
      ],
    },
    {
      id: 'video-convert',
      title: '영상 — 모든 화면이 트는 모양으로 바꿉니다',
      blocks: [
        {
          kind: 'text',
          text: '대행사가 주는 원본(MOV·ProRes·HEVC, 4K, H.264 High 등)은 매장 셋톱에서 멈추거나 검게 나올 수 있습니다. 서버는 올린 영상을 살펴보고, 그대로 틀 수 없으면 **모든 화면이 트는 모양(H.264 · 1080p · AAC MP4)으로 바꾼 재생본**을 만들어 화면에 내보냅니다. 원본은 그대로 보관합니다. 영상은 MP4 · MOV · WebM · MKV · AVI 를 올릴 수 있습니다.',
        },
        {
          kind: 'legend',
          title: '콘텐츠에 붙는 표시',
          items: [
            { label: '변환 대기', tone: 'warn', meaning: '변환 차례를 기다립니다. 그동안은 편성에 넣어 두어도 화면에 나가지 않습니다.' },
            { label: '변환 중 42%', tone: 'warn', meaning: '바꾸는 중입니다. 목록이 저절로 새로 고쳐집니다. 끝나면 표시가 사라지고 화면에 나갑니다.' },
            { label: '다시 변환 중', tone: 'info', meaning: '이미 나가던 영상을 다시 바꾸는 중입니다. 다 될 때까지 원본이 계속 나갑니다.' },
            { label: '변환 실패', tone: 'danger', meaning: '세 번 해 보고 실패했습니다. 마우스를 올리면 까닭이 보입니다. 원본을 다른 형식(MP4)으로 내보내 다시 올리세요.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '예전에 올린 영상이 일부 화면에서 멈춘다면',
          text: '변환 기능이 생기기 전에 올린 영상은 원본 그대로 나갑니다. 콘텐츠 상세 오른쪽 위의 [[…]] ▸ [[다시 변환]] 을 누르면(변환이 실패한 영상은 [[다시 변환]] 단추가 바로 보입니다) 재생본을 만들어 바꿔 줍니다 — 다 될 때까지는 원본이 계속 나가고, 다 되면 화면이 새 파일을 받습니다. 상세의 “재생본” 줄에서 지금 무엇이 나가는지와 변환한 까닭을 봅니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '큰 영상도 화면이 끝까지 받습니다',
          text: '화면은 큰 영상을 조각으로 나눠 받아, 매장 회선이 끊겨도 받은 데부터 이어 받습니다. 따로 할 일은 없습니다. 변환은 서버의 CPU 를 써서 영상 길이에 따라 몇 초에서 수십 분 걸립니다.',
        },
      ],
    },
    {
      id: 'validity',
      title: '게시 기간 — 정한 날까지만 나가게',
      blocks: [
        {
          kind: 'text',
          text: '계약 기간이 정해진 광고·행사 안내처럼 **언제까지만 나가야 하는** 콘텐츠에 게시 기간을 정합니다. 시작 전과 종료 시각부터는 **어느 플레이리스트·스케줄·캠페인에 들어 있어도 화면에서 빠집니다** — 끝난 광고를 사람이 일일이 빼러 다니지 않아도 됩니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '올릴 때: [[콘텐츠 추가]] 메뉴 맨 위의 [[업로드 옵션]] 에서 “게시 종료” 칸에 날짜·시각을 넣고 파일을 올립니다.' },
            { title: '올린 뒤: 카드의 [[편집]] 을 눌러 [[게시 기간]] 에 게시 시작·게시 종료를 넣고 [[저장]] 합니다.', body: '비운 칸은 그쪽 끝에 제한이 없습니다. 종료 시각부터 빠집니다.' },
            { title: '여러 개를 한꺼번에: 카드를 체크한 뒤 선택 막대의 [[게시 기간]] 을 누릅니다.', body: '캠페인 소재 묶음을 같은 계약 기간으로 맞출 때 씁니다.' },
          ],
        },
        {
          kind: 'legend',
          title: '목록의 표시',
          items: [
            { label: 'D-3 종료', tone: 'warn', meaning: '7일 안에 게시가 끝납니다. 오늘 끝나면 “오늘 18:00 종료” 로 보입니다. 대시보드의 “곧 끝나는 콘텐츠” 카드에도 나옵니다.' },
            { label: '게시 종료', tone: 'danger', meaning: '게시가 끝나 화면에 나가지 않습니다. 기간을 늘리거나 보관하세요.' },
            { label: '6. 16. 09:00 시작', tone: 'neutral', meaning: '아직 시작 전이라 나가지 않습니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '“전체 게시 기간” 거르기',
          text: '목록 위 [[필터]] 의 “게시 기간” 으로 “7일 안에 게시 종료”·“게시 종료됨”·“게시 시작 전” 만 골라 봅니다. 게시 기간이 바뀐 화면은 그 시각에 맞춰 저절로 넘어갑니다(서버에 닿지 않아도 미리 받은 시간표대로).',
        },
      ],
    },
    {
      id: 'web',
      title: '웹 페이지 등록하기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[콘텐츠 추가]] → [[웹 URL 추가]] 를 고릅니다.' },
            { title: '제목과 주소(https://…)를 입력하고, 필요하면 태그도 입력합니다.', body: '태그는 쉼표로 구분합니다.' },
            { title: '[[추가]] 를 누릅니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '사내 전용 페이지는 허용한 주소만 등록할 수 있습니다',
          text: '공공장소 화면에 사내 페이지가 노출되는 사고를 막기 위해 기본으로는 인터넷에 공개된 주소만 등록할 수 있습니다. 사내 포털·사내 대시보드를 띄우려면 관리자가 [[환경 설정]] 의 “웹 콘텐츠 허용 주소” 에 그 주소를 적어야 합니다. 콘솔의 웹 미리보기는 스크립트를 돌리지 않고 그린 모습이라 실제 화면과 다를 수 있으니, 실제 화면에서 확인하세요.',
        },
      ],
    },
    {
      id: 'html',
      title: 'HTML — 직접 만든 화면 파일 올리기',
      blocks: [
        {
          kind: 'text',
          text: '사내에서 따로 만든 현황판·배너처럼 **HTML 파일 하나로 된 화면**은 이미지·영상과 같은 자리에서 올립니다 — [[콘텐츠 추가]] → [[파일 업로드]] 에서 .html · .htm 파일(10MB까지)을 고르면 됩니다. HTML 파일이 없어도 [[콘텐츠 추가]] → [[빈 화면에서 만들기]] 로 직접 만들 수 있습니다(아래 “화면 편집”). 글자는 UTF-8 로 맞춰 저장합니다(EUC-KR 처럼 문서가 밝힌 다른 글자 집합도 읽습니다). 목록·편성 고르기·모니터링·화면 상세의 그림은 그 HTML 을 실제로 돌려 그린 모습입니다(스크립트로 그리는 내용까지).',
        },
        {
          kind: 'steps',
          title: '화면에 맞추기',
          steps: [
            { title: '올리고 나면 **화면 맞춤** 창이 저절로 뜹니다.', body: '여러 개를 함께 올렸으면 하나씩 넘겨 봅니다. 나중에는 목록 카드의 [[화면 맞춤]] 이나 콘텐츠 상세에서 다시 엽니다.' },
            { title: '이 HTML 을 띄울 **적용할 화면**을 고릅니다.', body: '등록된 화면이 보고한 크기(세운 화면이면 세운 크기)로 미리 봅니다. 미리보기 위에 “✓ 다 보입니다” 나 “✕ 아래 245px 잘림” 처럼 잘리는지가 바로 적힙니다.' },
            { title: '잘리면 [[화면에 다 맞추기]] 를 누릅니다.', body: '잘리지 않는 가장 큰 크기를 찾아 줍니다. 손으로는 크기 − + 로 고칩니다. 기준 크기(너비·높이)와 맞춤 방식(맞춤 — 다 보이게 · 채움 — 꽉 차게 · 그대로)은 **세부 설정** 에 있습니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '저장해야 화면에 나갑니다. 자기 모니터 크기(예: 1366×768)에 맞춰 고정으로 만든 HTML 도 다른 크기의 화면에서 줄이거나 늘려 다 보이게 띄웁니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '남이 만든 스크립트가 도는 콘텐츠라 칸 안에 가둡니다',
          text: 'HTML 은 화면에서 **안전한 칸(샌드박스)** 안에서 돕니다 — 스크립트는 돌지만 재생 프로그램·기기·콘솔에는 손대지 못하고, 새 창·페이지 이동·입력 양식이 막히며, 밖으로는 https 주소만 엽니다(사내망의 http 장비에는 닿지 않습니다). HTML 은 에이전트 0.7.17 이상인 화면에만 나가고(그 전 판에는 편성에서 빠집니다), 화면 맞춤은 0.7.18 부터 따릅니다 — 그 전 판은 화면 크기 그대로 띄웁니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '바깥 주소에서 불러오는 HTML 은 인터넷이 있어야 다 나옵니다',
          text: 'HTML 이 CDN 같은 바깥 주소에서 글꼴·그림·스크립트를 불러오면 콘텐츠 상세와 올린 직후 안내, 템플릿의 고르기 칸에 **“바깥 주소 N곳에서 불러옵니다”** 와 그 주소가 나옵니다. 인터넷이 끊긴 현장에서는 그 부분이 빠지고, 그 주소의 파일이 바뀌면 콘솔에서 승인한 뒤에도 화면이 바뀝니다 — 되도록 파일 안에 담아 올리세요. http 주소는 화면에서 열리지 않아 따로 짚어 줍니다. 아예 막으려면 [[환경 설정]] 의 “올린 HTML 의 바깥 연결” 을 씁니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '옛 셋톱에서도 스크립트가 돌게 올릴 때 손봅니다',
          text: '안드로이드 5.1 셋톱은 요즘 문법으로 짠 스크립트를 읽지 못하고, 칸 안에서는 브라우저 저장소(localStorage)도 막힙니다. 그래서 HTML 을 올리거나 [[화면 편집]] 에서 저장할 때 서버가 스크립트를 옛 셋톱도 읽는 모양으로 바꾸고 저장소 대신 쓸 것을 넣어 둡니다 — 날짜를 세는 현황판 같은 계산이 어느 화면에서나 돕니다. 저장소에 적은 값은 화면이 그 콘텐츠를 다시 틀 때마다 처음으로 돌아가니, 늘 같아야 하는 값(시작일 등)은 [[화면 편집]] 에서 입력 칸의 값으로 정해 두세요. 2026-10-30 전에 올린 HTML 은 [[화면 편집]] 에서 한 번 저장하거나 다시 올리면 됩니다.',
        },
      ],
    },
    {
      id: 'pdf',
      title: 'PDF — 문서를 쪽마다 이미지로 올리기',
      blocks: [
        {
          kind: 'text',
          text: '안내문·교육 자료처럼 **PDF 로 된 문서**도 이미지·영상과 같은 자리에서 올립니다 — [[콘텐츠 추가]] → [[파일 업로드]] 에서 .pdf 파일을 고르면 됩니다. 콘솔이 **쪽마다 이미지로 바꿔** 올리므로 화면에서는 보통 이미지 콘텐츠로 나갑니다(에이전트 판과 상관없이 모든 화면). 쪽 이미지는 긴 변 1920 픽셀로, 가로 화면에서도 세로 화면에서도 꽉 차게 보입니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[콘텐츠 추가]] → [[파일 업로드]] 에서 PDF 를 고릅니다.', body: '쪽을 이미지로 바꾸는 동안 “PDF 쪽을 그림으로 바꾸는 중 3/12” 처럼 진행이 보입니다. 끝날 때까지 콘솔 화면을 닫지 마세요 — 바꾸는 일은 이 브라우저에서 합니다.' },
            { title: '쪽마다 이미지 콘텐츠가 생깁니다.', body: '제목은 “안전수칙.pdf (1/12)” 처럼 파일 이름과 쪽 번호입니다. 한 쪽짜리 PDF 는 이미지 하나가 됩니다.' },
            { title: '여러 쪽이면 PDF 이름의 플레이리스트가 함께 생깁니다.', body: '쪽 순서대로 담기고 노출 시간은 이미지 기본값입니다. 이 플레이리스트를 화면에 배정하면 문서가 한 쪽씩 넘어갑니다. 플레이리스트를 만들려면 편성 권한이 필요합니다 — 없으면 쪽 이미지만 올라갑니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '올릴 수 없는 PDF',
          text: '**암호가 걸린 PDF** 는 바꿀 수 없습니다 — 암호를 풀어 다시 저장한 뒤 올리세요. 한 번에 **100쪽까지** 바꿉니다. 같은 PDF 를 다시 올리면 기존 콘텐츠를 그대로 쓰고 플레이리스트도 새로 만들지 않습니다. 화면 바로 보내기에서도 PDF 를 끌어다 놓으면 쪽 순서대로 골라집니다.',
        },
      ],
    },
    {
      id: 'template',
      title: '템플릿으로 공지·홍보 화면 만들기',
      blocks: [
        {
          kind: 'text',
          text: '디자인 프로그램 없이도 보기 좋은 화면을 만들 수 있습니다. 디자인은 정해져 있고 **글자만 채우면** 됩니다. 입력하는 동안 오른쪽 미리보기에 실제 모습이 바로 나타납니다.',
        },
        {
          kind: 'table',
          header: ['템플릿', '이럴 때 쓰세요'],
          rows: [
            ['공지사항', '인사·총무 공지처럼 한 건을 확실히 읽혀야 할 때'],
            ['프로모션', '이벤트·혜택 신청을 유도할 때 (마감 카운트다운 포함)'],
            ['긴급알림', '점검·장애처럼 보는 사람이 바로 행동해야 할 때'],
            ['일정/이벤트', '로비·회의실 앞에서 오늘 일정과 행사를 안내할 때'],
            ['대시보드', '실적·진행 현황을 늘 띄워 둘 때'],
            ['반반 화면', '제품·현장 사진이나 영상, 올린 HTML 화면을 보여 주며 설명할 때 (화면 반은 이미지·영상·HTML, 반은 글)'],
            ['무재해 현황', '무재해 시작일과 목표 배수만 정해 두고 그날의 달성 현황(달성 예정일·달성 일수·달성률·D-day)을 늘 띄워 둘 때'],
            ['구역 나누기', '현황판 HTML·홍보 영상·공지 사진처럼 여러 내용을 한 화면에 늘 함께 띄울 때 (화면을 2~4 구역으로)'],
          ],
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[콘텐츠 추가]] → [[템플릿 만들기]] 를 고르고, 사용할 종류를 누릅니다.', body: '“기본 템플릿” 여덟 가지 위에, 우리가 등록해 둔 화면이 있으면 “등록한 템플릿” 으로 먼저 나옵니다(아래 도움말).' },
            { title: '맨 위 “제목” 에 목록에서 알아볼 이름을 입력합니다.', body: '이 제목은 화면에 나오지 않고 콘솔 목록에서만 쓰입니다.' },
            { title: '“글꼴” 에서 세 가지 중 하나를 고릅니다.', body: '**Pretendard**(기본 — 깔끔한 고딕) · **나눔스퀘어 네오**(넓고 단단한 고딕 — 멀리서 읽히는 제목용) · **본명조**(격식 있는 명조 — 공지·행사 안내용). 고르면 미리보기가 바로 그 글꼴로 바뀌고, 화면에서도 같은 글꼴로 나옵니다. 화면 프로그램(에이전트)이 0.7.6 이상이어야 하며, 그 전 판은 기기에 깔린 글꼴로 그립니다.' },
            { title: '각 칸을 채우며 오른쪽 미리보기를 확인합니다.', body: '예시 내용이 미리 들어 있으니 고쳐 쓰면 됩니다. 목록형 칸은 [[↑]] [[↓]] 로 순서를 바꾸고 [[제거]] 로 뺍니다. 화면에 나타날 때 머리글이 내려앉고 카드·목록·막대가 차례로 떠올라 1초 남짓 만에 멈춥니다 — [[움직임 다시 보기]] 로 다시 봅니다. 나타난 뒤에도 요소마다 계속 움직입니다 — 진행 막대·막대 그래프에 빛이 흐르고, 카드에 빛이 스치고, 목록 줄이 차례로 밝아지며 왼쪽에 흰 띠가 서고, 아이콘이 둥실 뜨고, 배지·경보 아이콘에 물결이 퍼집니다. 글은 흔들리지 않고, 그리기가 느린 기기에서는 떠 있는 동안의 움직임이 저절로 꺼집니다(새 판 에이전트 화면부터).' },
            { title: '미리보기 위에 “입력 정상” 이 보이면 [[만들기]] 를 누릅니다.' },
            { title: '나중에 고치려면 카드의 [[편집]] 을 누르고, 고친 뒤 [[저장]] 합니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '저장하면 재생 중인 화면도 바뀝니다',
          text: '이미 플레이리스트에 들어가 있는 템플릿을 고쳐 저장하면, 그 템플릿을 재생 중인 **모든 화면에 곧바로** 반영됩니다. 오타가 없는지 미리보기로 확인한 뒤 저장하세요. 잘못 저장했으면 편집기의 [[변경 이력]] 에서 앞 판을 골라 [[되돌리기]] 를 누릅니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '반반 화면 — 사진·영상·HTML 옆에 설명을',
          text: '“그림·영상·HTML” 에서 [[콘텐츠에서 고르기]] 로 올려 둔 이미지·영상·HTML 을 고르거나 [[내 컴퓨터에서 올리기]] 로 바로 올립니다(콘텐츠로도 함께 올라갑니다). [[맞추기]] 는 가득 채우기(칸을 꽉 채우고 넘치는 곳은 잘림)·전체 보이기(다 보이고 남는 곳은 검게)입니다. **HTML** 을 고르면 그 칸에서 HTML 이 스크립트까지 돌고, HTML 에 넣은 영상도 소리 없이 나옵니다 — 고정 크기로 만든 HTML 은 맞추기대로 칸에 맞춰 줄이고, 화면 크기에 맞춰 늘고 주는 HTML 은 칸 크기에 맞춰 다시 짜입니다. HTML 을 넣은 반반 화면은 HTML 칸을 그릴 줄 아는 새 판 에이전트 화면에만 나가고, [[화면 편집]] 으로 옮긴 사본에는 HTML 칸이 장식 무늬로 남습니다. “나누는 방향” 의 **화면에 맞춰** 는 가로 화면에서 좌우로, 세로 화면에서 위아래로 나눕니다 — 좌우·위아래로 고정할 수도 있고, “그림 자리” 로 이미지를 오른쪽(아래)에 둘 수도 있습니다. 반대쪽 카드에는 배지·제목·설명·핵심 항목(4줄까지)·아래 한 줄이 나옵니다. 영상은 소리 없이 되풀이합니다. 화면은 편성을 받을 때 그 이미지·영상도 함께 받아 두어 인터넷이 끊겨도 나옵니다. **에이전트를 새 판으로 올린 화면에만 나갑니다**(옛 판 화면은 이 템플릿을 건너뜁니다). 템플릿에 넣은 이미지·영상·HTML 은 콘텐츠 목록에서 지울 수 없습니다 — 먼저 템플릿에서 바꾸거나 뺍니다. **“그림 칸 크기”** 로 그림 칸을 40·50·60·70% 로 고릅니다 — 1920×1080 으로 만든 HTML 이 반쪽 칸에서 작게 보이면 키우고, 글 칸이 좁아지면 글이 한 단계씩 작아집니다(이것을 모르는 옛 판 화면은 반반으로 그립니다). 고정 크기로 만든 HTML 은 **“보일 영역”** 에서 그 HTML 위를 끌어 네모를 그리면 그 부분만 칸에 크게 나옵니다 — [[전체]] 로 되돌립니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '구역 나누기 — 한 화면에 여러 내용을 함께',
          text: '“나누는 모양” 에서 **둘로 똑같이 · 크게 하나 + 옆 하나 · 크게 하나 + 옆 둘 · 셋으로 똑같이 · 넷으로(2×2)** 가운데 하나를 그림을 보고 고릅니다(세로 화면은 위아래로 쌓입니다). 구역마다 [[그림·영상·HTML]] 과 [[글]] 가운데 하나를 고르고, 구역 제목 아래에 그 구역의 가로·세로 화면 크기가 나옵니다 — HTML 을 그 크기로 만들면 꽉 찹니다. 그림 구역에 “장 더하기” 로 여러 장을 넣으면 “넘기는 간격” 마다 다음 장으로 넘어가고, 같은 내용을 트는 화면끼리 함께 넘어갑니다. 장마다 [[맞추기]] 를 고르고, HTML 은 반반 화면처럼 “보일 영역” 을 고를 수 있습니다. **영상은 한 화면에 하나 — 그 구역에 혼자** 넣고 소리 없이 되풀이합니다. **HTML 은 한 화면에 둘까지**입니다(화면 기기가 버거워집니다). 글 구역에는 배지·제목·설명이 나오고, 구역이 작으면 글도 작아집니다. 모양을 바꿨다 되돌리거나 글로 바꿨다 되돌려도 편집 창을 닫기 전에는 하던 것이 남습니다. 늘 띄워 두는 화면이라 기본 노출 시간은 60초입니다 — 이것 하나만 담은 플레이리스트를 배정하세요. [[화면 편집]] 으로 옮긴 사본에는 그 순간 보이는 장 하나만 담깁니다. **에이전트를 새 판으로 올린 화면에만 나갑니다**(옛 판 화면은 이 템플릿을 건너뜁니다).',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '무재해 현황 — 시작일과 목표 배수만 정해 둡니다',
          text: '“무재해 시작일” 달력에서 날짜를, “목표 배수” 에서 배수를 고릅니다. 목표 기간은 앞 배수를 채운 다음 날부터 이 배수를 채우는 날까지입니다(1~5배수는 한 해씩, 5→7배수는 두 해, 7→10배수는 세 해). 화면이 그날 날짜로 달성 예정일·전체 일수·현재 달성 일수·달성률·D-day 와 누적 무재해 일수를 세므로 **날이 바뀌어도 고칠 것이 없습니다** — 배수를 채우면 다음 배수로 바꿔 저장합니다. 배지·제목·부서는 위 큰 카드에 나오고, 배지·부서는 비우면 빠집니다. **에이전트를 새 판으로 올린 화면에만 나갑니다**(옛 판 화면은 이 템플릿을 건너뜁니다).',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '잘 만든 화면은 템플릿으로 등록해 두고 사본으로 새로 만듭니다',
          text: '올린 HTML 이나 템플릿 콘텐츠의 상세에서 오른쪽 위 [[…]] ▸ [[템플릿으로 등록]] 을 누르고 이름과 설명을 적으면, [[콘텐츠 추가]] ▸ [[템플릿 만들기]] 맨 위 “등록한 템플릿” 에 그림과 함께 나옵니다. 고르면 HTML 은 새 콘텐츠 이름을 정한 뒤 그 사본이 [[화면 편집]] 으로, 템플릿은 같은 내용이 채워진 편집기로 열리고, 저장하면 새 콘텐츠가 됩니다 — 원래 콘텐츠는 그대로입니다(예: 1공장 현황판을 등록해 두고 2공장 현황판을 만듭니다). 등록한 콘텐츠는 제목 옆에 “템플릿으로 등록됨” 이 붙고, 같은 메뉴에 [[템플릿 이름·설명 고치기]] · [[템플릿 등록 해제]] 가 있습니다. 템플릿 내용이 원본과 똑같으면 새로 만들지 않으니 바꿀 곳을 고친 뒤 저장하세요.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '회사 이름·로고·색·글꼴은 환경 설정에서 한 번에',
          text: '템플릿 머리글의 조직 이름·로고와 강조 색은 [[환경 설정]] 의 “브랜드” 가 모든 템플릿에 함께 넣습니다. 사내 글꼴을 올려 두면 편집기의 “글꼴” 에서 “사내 글꼴” 도 고를 수 있습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '템플릿을 만들기 전에 알아둘 점',
          text: '모든 템플릿은 화면 아래에 흐르는 “하단 티커” 내용이 필수입니다. 프로모션 템플릿의 QR 무늬는 장식이라 스캔되지 않습니다. 템플릿 종류는 만든 뒤에 바꿀 수 없습니다.',
        },
      ],
    },
    {
      id: 'screen-edit',
      title: '화면 편집 — 보면서 옮기고 크기 바꾸기',
      blocks: [
        {
          kind: 'text',
          text: '올린 HTML 과 템플릿은 코드를 몰라도 **화면을 보면서** 고칠 수 있습니다. 콘텐츠 상세의 [[화면 편집]] 을 누르면 실제 화면이 그대로 열립니다 — 가운데는 화면, 위는 넣기·되돌리기·저장, 오른쪽은 고른 것의 색과 크기입니다. 보이는 것을 누르면 그것이 골립니다. **빈 화면에서 새로 만들 수도 있습니다** — 콘텐츠 목록의 [[콘텐츠 추가]] ▸ [[빈 화면에서 만들기]] 에서 이름과 화면 방향(가로 1920×1080 · 세로 1080×1920)을 고르면 같은 편집기가 흰 화면으로 열리고, [[새 콘텐츠로 저장]] 하면 새 HTML 콘텐츠가 됩니다(업로드 옵션에 적어 둔 태그가 붙습니다).',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '콘텐츠 상세에서 [[화면 편집]] 을 누릅니다.', body: '마우스를 올리면 누르면 골릴 것에 파란 점선과 이름(글·그림·상자)이 보입니다.' },
            { title: '고칠 것을 **누릅니다**.', body: '글을 누르면 그 글, 그림을 누르면 그 그림, 상자의 빈 곳을 누르면 그 상자가 골립니다. 카드 전체처럼 더 큰 묶음이 필요하면 오른쪽 칸의 [[감싼 상자 고르기]] 를 누릅니다.' },
            { title: '**끌어서** 옮기고, 테두리의 **동그라미**를 끌어 크기를 바꿉니다.', body: '다른 요소의 변·가운데에 가까워지면 붙으며 빨간 맞춤선이 보입니다. 그림은 모서리로 끌면 비율이 그대로입니다. 방향키로 1픽셀(Shift 는 10픽셀)씩 옮길 수도 있습니다.' },
            { title: '글은 고른 다음 **한 번 더 누르면** 고칩니다.', body: '고른 것 위에 뜨는 [[글 고치기]] 를 눌러도 됩니다. 다 쓰면 바깥을 누르거나 [[다 고쳤습니다]] 를 누릅니다.' },
            { title: '색·크기는 **오른쪽 칸**에서 바꿉니다.', body: '고른 것에 맞는 것만 보입니다 — 글이면 글자 크기(−·+)·굵게·정렬·글자 색·글꼴, 그림이면 [[다른 그림으로 바꾸기]], 입력 칸이면 값과 글자, 모두 바탕 색·모서리 둥글게·진하기·가로·세로 가운데·앞으로·뒤로. 색은 맨 앞의 “이 화면의 색” 을 고르면 화면과 어울립니다.' },
            { title: '입력 칸·선택 상자에는 **값**을 정합니다.', body: '날짜 입력을 고르면 오른쪽 칸 “값” 에 달력이, 선택 상자는 고를 항목이 나옵니다. 화면이 뜰 때 이 값이 들어가고, 무재해 현황판처럼 이 값으로 계산하는 화면은 그대로 계산합니다 — 기기에 전에 저장된 값이 있어도 정한 값이 앞섭니다. 편집 화면에서는 스크립트가 돌지 않아 계산된 숫자가 비어 보입니다. 선택 상자의 항목은 한 줄에 하나씩 고치고, 입력 칸은 비었을 때 보이는 안내 글도 정합니다.' },
            { title: '**그림 영역**에 내 컴퓨터의 그림을 넣습니다.', body: '[[넣기]] ▸ [[그림 영역]] 으로 자리와 크기를 먼저 잡고, 고른 뒤 [[그림 고르기]](두 번 눌러도 됩니다)로 파일을 고릅니다. 큰 사진은 긴 변 2048픽셀로 줄여 넣고, 그림은 이 화면 안에 함께 저장됩니다(줄인 뒤 3MB까지). [[가득 채우기]] · [[전체 보이기]] 로 맞추고 [[그림 빼기]] 로 비웁니다.' },
            { title: '**영상**도 넣습니다.', body: '[[넣기]] ▸ [[영상]] 에서 내 컴퓨터의 영상을 고르면 콘텐츠로도 올라가고(화면이 트는 모양으로 바뀝니다, 큰 영상은 몇 분) 보이는 화면의 가운데에 놓입니다. 소리 없이 되풀이해 틉니다. 오른쪽 칸에서 [[가득 채우기]] · [[전체 보이기]] 를 고르고, [[소리 없이]] 를 누르면 소리를 냅니다. [[다른 영상으로 바꾸기]] 는 자리·크기를 그대로 둡니다. 화면 기기는 HTML 과 함께 그 영상을 받아 두고 그 자리에 틉니다 — **새 에이전트가 필요**하고, 옛 에이전트에서는 영상 자리가 검게 남습니다. 넣은 영상은 화면에서 맨 위에 그려지므로 영상 위에 글을 겹치지 마세요. HTML 에 넣은 영상은 콘텐츠 목록에서 지울 수 없습니다(먼저 화면 편집에서 뺍니다).' },
            { title: '**D-day** 를 넣으면 날짜를 고르는 즉시 셈이 보입니다.', body: '[[넣기]] ▸ [[D-day]] 를 고른 뒤 오른쪽 칸에서 기준일을 [[날짜 고르기]] 로 정하거나, [[화면의 날짜 칸]] 으로 이 화면의 날짜 입력 칸(예: 무재해 시작일)에 잇습니다. 셈은 [[남은 날]](D-12 · 그날 D-DAY · 지나면 D+3)과 [[지난 날]](기준일을 1일째로, 예: 무재해 609일째)이고, 앞 글·뒤 글을 붙입니다. 이은 날짜 칸의 값을 바꾸면 편집 화면에서도 바로 다시 셉니다. 화면 기기는 켤 때와 자정마다, 그리고 그 날짜 칸을 바꿀 때 다시 셉니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '잘못 고쳤으면 위의 [[되돌리기]] 를 누르거나, 고른 것을 [[이것만 원래대로]] 로 돌립니다. 아무것도 고르지 않으면 오른쪽 칸의 [[처음 모습으로 모두 되돌리기]] 가 보입니다. 저장하지 않고 닫으면 고친 것이 사라진다고 한 번 묻습니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['자리', '하는 일'],
          rows: [
            ['위 막대', '[[넣기]] — 글(제목·글·목록·단추·D-day) · 그림·모양(그림 영역·그림·영상·상자·구분선·표) · 입력(입력 칸·숫자 입력·날짜 입력·선택 상자·체크 상자)을 골라 보이는 화면의 가운데에 놓습니다. [[되돌리기]] · [[다시 하기]] · [[닫기]] · [[저장]].'],
            ['고른 것 위', '[[글 고치기]](글) · [[그림 바꾸기]](그림) · [[그림 고르기]](그림 영역) · [[복제]] · [[지우기]]. 오른쪽 클릭하면 같은 일과 앞으로·뒤로가 나옵니다.'],
            ['가운데 화면', '빈 곳을 끌면 화면이 옮겨집니다. 왼쪽 아래의 −·+ 로 확대·축소하고 [[화면에 맞추기]] 로 돌아옵니다(휠로 옮기기, Ctrl·Command+휠로 확대도 됩니다).'],
            ['오른쪽 칸', '고른 것의 색·크기·자리. 아무것도 고르지 않으면 쓰는 법과 화면 크기·화면 배경 색이 보입니다.'],
          ],
        },
        {
          kind: 'table',
          header: ['고치는 것', '저장하면'],
          rows: [
            ['올린 HTML', '**같은 콘텐츠**가 고친 내용으로 바뀝니다. 그 콘텐츠를 쓰던 플레이리스트·스케줄은 그대로 두어도 화면이 새 내용을 받아 틉니다. 화면 크기에 맞춰 늘고 주는 HTML 은 고른 편집 크기(가로 1920×1080 · 세로 1080×1920)로 굳혀 화면마다 통째로 맞춰 띄웁니다.'],
            ['템플릿', '템플릿의 지금 모습을 옮긴 **새 HTML 콘텐츠** “제목 (편집)” 이 생기고 원래 템플릿은 그대로 남습니다. 저장 단추 아래 “플레이리스트 N개에서 바꿔 넣기” 를 켜 두면 그 템플릿이 들어 있던 플레이리스트의 자리에 새 콘텐츠가 들어갑니다(편성 권한이 있을 때).'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '알아 둘 점',
          text: '템플릿 사본에서는 시계와 남은 시간만 저절로 바뀌고, 글은 [[내용 편집]] 이 아니라 화면 편집에서 고칩니다. 머리글의 장소 이름은 비워 둡니다(어느 화면에나 같은 글이 나가기 때문입니다). HTML 이라 에이전트 0.7.17 이상인 화면에만 나갑니다. 올린 HTML 의 스크립트는 편집 화면에서는 돌지 않아 스크립트가 그리는 숫자가 비어 보일 수 있지만, 화면에서는 그대로 돕니다. 격자·가로 줄로 늘어선 요소는 크기를 바꾸면 옆 요소도 함께 밀리거나 줄어듭니다 — HTML 이 원래 그렇게 놓이기 때문입니다. 단축키는 되돌리기(Ctrl+Z)·다시 하기(Ctrl+Shift+Z)·복사(Ctrl+C)·붙여넣기(Ctrl+V)·복제(Ctrl+D)·지우기(Delete) 정도만 씁니다.',
        },
      ],
    },
    {
      id: 'organize',
      title: '찾기·보관·삭제',
      blocks: [
        {
          kind: 'actions',
          title: '목록 위의 도구',
          items: [
            { label: '제목 검색', text: '제목의 일부만 넣어도 찾습니다.' },
            { label: '전체 형식', text: '이미지·영상·템플릿·웹 중 하나만 골라 봅니다.' },
            { label: '전체 태그', text: '붙여 둔 태그로 걸러 봅니다. 괄호 안 숫자는 그 태그가 붙은 콘텐츠 수입니다. 태그를 하나도 쓰지 않으면 이 칸이 나오지 않습니다.' },
            { label: '필터', text: '태그·게시 기간·보관함으로 거릅니다. “보관함 보기” 를 체크하면 보관해 둔 콘텐츠도 흐리게 함께 보입니다. 걸려 있는 거르개 수가 단추에 붙습니다(예: “필터 1”).' },
            { label: '정렬', text: '그리드에서는 목록 위의 정렬 칸에서, 목록 보기에서는 **제목·크기·편성·등록** 열 이름을 눌러 고릅니다. “편성 적은 순” 이면 아무 데도 쓰지 않는 콘텐츠가 먼저 모여 정리하기 좋습니다.' },
          ],
        },
        {
          kind: 'text',
          text: '콘텐츠가 24개를 넘으면 목록 아래에 **쪽 번호**가 나옵니다. 번호나 양옆 화살표를 눌러 넘기고, 오른쪽 칸에서 한 쪽에 볼 개수(24·48·96개)를 고릅니다 — 고른 개수는 이 브라우저에 기억됩니다. 정렬은 쪽마다가 아니라 콘텐츠 전체에 적용됩니다. 검색·필터·정렬을 바꾸면 첫 쪽으로 돌아가고, 콘텐츠 상세에서 뒤로 오면 보던 쪽으로 돌아옵니다. 고른 콘텐츠는 쪽을 넘겨도 남고, 선택 막대에 “다른 쪽 N건 포함” 으로 그 수가 붙습니다.',
        },
        {
          kind: 'text',
          text: '카드 아래의 “편성: …” 줄은 이 콘텐츠가 어느 플레이리스트에 들어 있는지 알려줍니다. “편성에 없음” 이면 지금 어느 화면에도 나가지 않습니다. 카드를 누르면 **콘텐츠 상세**에서 미리보기, 기본 정보, 배포 현황을 볼 수 있습니다. 기본 정보의 [[태그]] 칸은 [[태그 추가…]] 목록에서 쓰이는 태그를 고르고, 없는 태그는 [[새 태그 직접 입력…]] 으로 적어 Enter 로 붙이며 × 로 뺍니다. **붙이거나 빼는 즉시 저장**됩니다.',
        },
        {
          kind: 'table',
          header: ['', '보관', '삭제'],
          rows: [
            ['무엇이 되나', '목록에서 숨겨질 뿐 남아 있습니다', '완전히 삭제됩니다'],
            ['되돌리기', '[[복원]] 으로 언제든', '**불가능**'],
            ['편성에 쓰이는 중이면', '보관할 수 있습니다', '막힙니다 — 먼저 플레이리스트에서 빼야 합니다'],
            ['이럴 때', '시즌이 지난 홍보물처럼 다시 쓸지도 모를 때', '다시 사용하지 않는 것이 확실할 때'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '여러 개를 한꺼번에 지우려면',
          text: '카드 왼쪽 위 체크박스로 여러 개를 고르면 위에 선택 막대가 나타납니다. [[삭제]] 를 누르면 편성에 쓰이지 않는 것만 지워지고, 쓰이는 것은 이름과 함께 “남았습니다” 로 표시합니다.',
        },
      ],
    },
  ],
};

export const PLAYLISTS: ManualChapter = {
  slug: 'playlists',
  title: '플레이리스트',
  summary: '콘텐츠를 재생할 순서대로 묶고, 하나마다 몇 초씩 보여줄지 정합니다.',
  menuPath: '/playlists',
  editCapability: 'schedule:write',
  sections: [
    {
      id: 'what',
      title: '플레이리스트란',
      blocks: [
        {
          kind: 'text',
          text: '음악 재생 목록과 같은 구조입니다. 담긴 콘텐츠를 **위에서부터 차례로** 재생하고, 끝까지 재생하면 처음으로 돌아가 반복합니다. 화면·스케줄·캠페인에는 콘텐츠가 아니라 이 플레이리스트를 배정합니다.',
        },
        { kind: 'figure', figure: 'playlist-loop', caption: '담긴 순서대로 정해진 시간만큼 보여주고, 끝나면 처음으로 돌아갑니다' },
      ],
    },
    {
      id: 'read',
      title: '목록 읽기',
      blocks: [
        {
          kind: 'text',
          text: '**항목** 열은 “재생되는 수 / 담긴 수” 입니다. 담아 두고 “사용” 체크를 끈 항목, 보관한 콘텐츠, 삭제된 콘텐츠는 화면에 나가지 않으므로 앞의 수에서 빠집니다. 빠진 항목이 있으면 뒤에 **“· N개 빠짐”** 이 붙습니다 — 목록에는 “2 / 2” 로 보이는데 화면에는 하나만 나가는 상황을 여기서 알아챌 수 있습니다. 빠진 항목을 되살리려면 편집에서 그 줄을 빼거나, 콘텐츠 보관함에서 복원하세요.',
        },
        {
          kind: 'text',
          text: '**총 길이** 는 목록을 한 번 재생하는 데 걸리는 시간으로, **화면에 실제로 나가는 항목만** 더합니다 — 꺼 둔 항목, 보관·삭제한 콘텐츠, 게시 기간 밖, 변환 중인 영상은 빠집니다. 영상은 노출 시간이 아니라 영상 길이로 셉니다(끝까지 재생하므로). 길이를 아직 모르는 영상이 있으면 “1분 3초 + 영상 1개” 처럼 따로 적습니다. 편집 창 머리의 “한 바퀴” 도 같은 값입니다.',
        },
        {
          kind: 'text',
          text: '목록은 최근 수정한 순으로 나옵니다. **이름**·**총 길이**·**수정** 열 이름을 누르면 그 열로 정렬하고, 위의 검색칸에 이름 일부를 넣으면 그 플레이리스트만 남습니다. **항목** 열의 필터 아이콘으로 “빠진 콘텐츠 있음” 을 고르면 콘텐츠를 보관·삭제한 뒤 손봐야 할 플레이리스트만 모아 볼 수 있습니다. 줄의 [[복제]] 는 담긴 콘텐츠·노출 시간·전환을 그대로 담은 사본(“이름 (사본)”)을 만들고 곧바로 그 편집 창을 엽니다. 왼쪽 체크칸으로 여러 개를 골라 한 번에 지울 수도 있습니다 — 화면·스케줄·캠페인이 쓰고 있는 것은 건너뛰고 그 이름을 목록 위에 남깁니다.',
        },
      ],
    },
    {
      id: 'create',
      title: '플레이리스트 만들기',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[플레이리스트]] 메뉴에서 [[새 플레이리스트]] 를 누르고, 이름을 입력해 [[만들기]] 를 누릅니다.', body: '이름은 나중에 바꿀 수 없으므로 “1층 로비 기본”, “점심 메뉴” 처럼 쓰임새가 드러나게 정하세요. 만들면 편집 창이 바로 이어서 열리므로 목록에서 다시 찾을 필요가 없습니다.' },
            { title: '편집 창 **오른쪽 “콘텐츠”** 에서 담을 콘텐츠를 **체크**합니다.', body: '체크하는 즉시 왼쪽 “재생 순서” 뒤에 담깁니다 — 따로 누를 단추는 없습니다. 체크를 풀면 그 콘텐츠가 목록에서 빠집니다. 카드마다 형식·제목과 함께 파일 크기·해상도·영상 길이(템플릿은 종류, 웹은 사이트 주소)가 적혀 있어 비슷한 그림이나 같은 영상의 다른 판도 가려 고를 수 있고, 이미 담은 카드에는 “담김”(두 번 담았으면 “담김 2”)이 붙습니다. 콘텐츠가 많으면 제목 검색과 **형식 필터**(이미지·영상·웹·템플릿)로 좁히세요.' },
            { title: '왼쪽에서 순서를 정합니다.', body: '줄 왼쪽의 번호 옆 ⠿ 손잡이를 끌어 옮기거나, [[↑]] [[↓]] 를 누릅니다(휴대폰에서는 ↑↓ 만 보입니다).' },
            { title: '콘텐츠마다 노출 시간(초)을 정합니다.', body: '모두 같은 시간·효과로 하려면 목록 머리의 “노출 시간” 칸에 초를, “전환” 칸에 효과를 정한 뒤 맨 오른쪽 [[전체 적용]] 을 누릅니다 — 누를 때 두 값이 모든 줄에 함께 들어갑니다(영상은 끝까지 재생하므로 노출 시간에서 빠집니다). 줄마다 값이 다르면 칸이 “혼합” 으로 보이고, 혼합인 채 둔 칸은 바꾸지 않습니다. 바꿀 것이 없으면 단추가 꺼져 있습니다. 적용한 뒤에도 [[저장]] 을 눌러야 화면에 반영됩니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '바꾼 내용이 없으면 저장 단추가 눌리지 않습니다.' },
          ],
        },
        {
          kind: 'actions',
          title: '항목 한 줄에서 할 수 있는 일',
          items: [
            { label: '번호', text: '재생 차례입니다. ⠿ 를 끌거나 ↑↓ 로 바꾸면 번호도 따라 바뀝니다.' },
            { label: '사용', text: '체크를 끄면 목록에는 남겨 두고 재생에서만 잠시 뺍니다. 행사 기간에만 넣고 빼는 콘텐츠에 쓰면 편리합니다.' },
            { label: 'fade', text: '다음 콘텐츠로 넘어갈 때의 효과입니다(아래 표). 목록 머리의 “전환” 으로 한 번에 맞출 수도 있습니다.' },
            { label: '✕', text: '이 플레이리스트에서만 뺍니다. 콘텐츠 자체는 남아 있습니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['전환', '화면에서'],
          rows: [
            ['페이드', '이전 화면 위로 다음 화면이 서서히 겹쳐 나타납니다. 기본값입니다.'],
            ['밀기', '다음 화면이 오른쪽에서 들어오며 이전 화면을 왼쪽으로 밀어냅니다.'],
            ['위로 밀기', '다음 화면이 아래에서 올라오며 이전 화면을 위로 밀어냅니다. 세로로 세운 화면에 잘 어울립니다.'],
            ['확대', '이전 화면이 앞으로 다가오고, 그 위로 다음 화면이 살짝 작은 데서 커지며 겹쳐 나타납니다.'],
            ['덮기', '다음 화면이 그림자를 드리운 카드처럼 오른쪽에서 미끄러져 와 덮고, 이전 화면은 살짝 물러나며 어두워집니다.'],
            ['뒤집기', '카드를 뒤집는 것처럼 화면이 세로 축으로 돌아 다음 화면이 나타납니다.'],
            ['대각선 타일', '이전 화면이 작은 칸으로 나뉘어 왼쪽 위부터 대각선을 따라 차례로 사라지며 다음 화면이 드러납니다. 웹 페이지에서 넘어갈 때는 페이드로 바뀝니다.'],
            ['없음', '곧바로 바뀝니다.'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '[[효과 미리보기]] 로 움직임을 먼저 봅니다',
          text: '편집 창의 “전환” 옆 [[효과 미리보기]] 를 누르면 효과마다 작은 화면이 A·B 를 번갈아 넘기며 실제 화면과 같은 움직임을 되풀이합니다. 카드의 [[이 효과 고르기]] 를 누르면 “전환” 칸에 들어가고(목록에는 [[전체 적용]] 을 눌러야 들어갑니다), 줄마다 있는 전환 칸에 마우스를 올리면 고른 효과의 설명이 뜹니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '옛 화면에서는 페이드로 바뀝니다',
          text: '페이드 말고 다른 효과는 에이전트를 새 판으로 올린 화면부터 그대로 보입니다. 옛 에이전트(0.7.7 이하)와 Apple TV 화면은 고른 효과 대신 페이드로 넘어갑니다 — 화면이 멈추거나 깨지지는 않습니다.',
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '[[취소]] 나 창 닫기는 묻지 않고 변경을 버립니다',
          text: '긴 목록을 정리하는 중이라면 중간중간 [[저장]] 하세요. 저장하면 이 플레이리스트를 재생 중인 화면에 곧바로 반영됩니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '잘못 저장했으면 앞 판으로 되돌립니다',
          text: '편집 창의 [[변경 이력]] 을 누르면 저장할 때마다 남은 판이 누가·언제·항목 몇 개와 함께 보입니다. 판을 펼쳐 확인하고 [[되돌리기]] 를 누르면 그때의 순서·노출 시간으로 돌아갑니다. 그 사이 지운 콘텐츠가 들어 있던 판은 되돌릴 수 없습니다.',
        },
      ],
    },
    {
      id: 'video',
      title: '영상은 끝까지 재생한 뒤 넘어갑니다',
      blocks: [
        {
          kind: 'text',
          text: '영상도 이미지·템플릿·웹과 **한 플레이리스트에 함께** 담을 수 있습니다. 영상은 노출 시간과 상관없이 **끝까지 재생된 뒤** 다음 콘텐츠로 넘어갑니다. 그래서 영상 줄에는 노출 시간 칸 대신 “끝까지 재생” 이 보이고, 제목 아래에 영상 길이가 적힙니다. 머리의 “한 바퀴” 시간도 영상 길이로 셉니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '예) 이미지 공지 사이에 홍보 영상 넣기',
          text: '공지 이미지 3장(각 8초)과 30초짜리 홍보 영상 하나를 담으면, 이미지 3장이 8초씩 나온 뒤 영상이 30초 끝까지 나오고 다시 첫 이미지로 돌아갑니다. 한 바퀴는 54초입니다.',
        },
      ],
    },
    {
      id: 'delete',
      title: '삭제하기',
      blocks: [
        {
          kind: 'callout',
          tone: 'warn',
          title: '[[삭제]] 는 되돌릴 수 없습니다',
          text: '줄 끝의 [[…]] ▸ [[삭제]] 를 누르면 한 번 묻고 지웁니다. 지운 플레이리스트는 되살릴 수 없습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '쓰는 곳이 있으면 어디인지 알려 줍니다',
          text: '화면 배정·바로 보내기·그룹·스케줄·캠페인·나가고 있는 긴급 송출에서 쓰고 있는 플레이리스트는 바로 지워지지 않고, “플레이리스트 삭제” 창에 **사용 중인 곳**이 종류별로 뜹니다. 이름을 누르면 그곳(화면 상세·스케줄·캠페인)으로 가니, 거기서 직접 바꾼 뒤 다시 삭제해도 됩니다. 끝난 캠페인에는 “종료됨” 이 붙습니다. 해제했거나 끝난 긴급 송출은 막지 않습니다.',
        },
        {
          kind: 'steps',
          title: '사용 중인 플레이리스트를 한 번에 삭제하기',
          steps: [
            { title: '“처리 방법” 에서 둘 중 하나를 고릅니다.', body: '**다른 플레이리스트로 교체**(권장) — “교체할 플레이리스트” 를 고르면 쓰던 화면·그룹·스케줄·캠페인이 모두 그것으로 바뀝니다. 화면이 비지 않습니다. **연결 해제** — 화면·그룹 배정은 비워지고(그 화면은 그룹 편성이나 대기 화면을 틉니다), 스케줄에서는 빠지며, 이 플레이리스트를 쓰던 **캠페인은 삭제됩니다**.' },
            { title: '“적용 결과” 에서 무엇이 몇 개 바뀌는지 확인하고 [[교체 후 삭제]] 또는 [[해제 후 삭제]] 를 누릅니다.', body: '바로 보내기로 걸려 있던 화면은 어느 쪽이든 보내기가 끝나고 평소 편성으로 돌아갑니다. 바뀐 스케줄·캠페인은 변경 이력에, 한 일은 변경 기록에 남습니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '진행 중인 긴급 송출이 쓰면 삭제할 수 없습니다',
          text: '사고 대응 중인 송출을 삭제 한 번으로 끄지 않도록 막아 두었습니다. 캠페인 화면의 긴급 패널에서 먼저 해제한 뒤 삭제하세요.',
        },
      ],
    },
  ],
};
